import { SubState } from '../state';
import { AppState } from '../state.module';
import { Phone } from '../../core/model/phone';
import { PhoneBrand } from '../../core/model/phone-brand';
import { PhoneModel } from '../../core/model/phone-model';
import { Customer } from '../../core/model/customer';
import { StepGuard } from '../../core/model/step-guard';
import { Estimate } from '../../core/model/estimate';
import { Payment } from '../../core/model/payment';

export class OrderState implements SubState {
  constructor(private _parent: AppState = null,
    private _step: StepGuard = new StepGuard(),
    private _phone: Phone = new Phone(),
    private _customer: Customer = new Customer(),
    private _estimationId: string = null,
    private _customerId: string = null,
    private _orderId: string = null,
    private _estimate: Estimate = null,
    private _paymentId: string = null,
    private _payment: Payment = new Payment(),
    private _invoicePdfId: string = null
  ) {
  }

  get parent(): AppState {
    return this._parent;
  }

  get step(): StepGuard {
    return this._step;
  }

  get phone(): Phone {
    return this._phone;
  }

  get customer(): Customer {
    return this._customer;
  }

  get estimationId(): string {
    return this._estimationId;
  }

  get orderId(): string {
    return this._orderId;
  }

  get customerId(): string {
    return this._customerId;
  }

  get estimate(): Estimate {
    return this._estimate;
  }

  get paymentId(): string {
    return this._paymentId;
  }

  get payment(): Payment {
    return this._payment;
  }

  get invoicePdfId(): string {
    return this._invoicePdfId;
  }


  withParent(parent: AppState): OrderState {
    return new OrderState(
      parent,
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    );
  }

  addCurrentWizzardStep(step: StepGuard): AppState {
    return this._parent.withOrder(this.recreate(
      step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addCustomerPhone(phone: Phone): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addBrandToCustomerPhone(brand: PhoneBrand): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone.withBrand(brand),
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addModelToCustomerPhone(model: PhoneModel): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone.withModel(model),
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addCustomerType(type: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer.withType(type),
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addCustomerDetails(customer: Customer): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addEstimationId(estimationId: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addEstimate(estimate: Estimate): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this.estimationId,
      this._customerId,
      this._orderId,
      estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addCustomerId(customerId: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addOrderId(orderId: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addPaymentId(paymentId: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      paymentId,
      this._payment,
      this._invoicePdfId
    ));
  }

  addPayment(payment: Payment): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      payment,
      this._invoicePdfId
    ));
  }

  addinvoicePdfId(invoicePdfId: string): AppState {
    return this._parent.withOrder(this.recreate(
      this._step,
      this._phone,
      this._customer,
      this._estimationId,
      this._customerId,
      this._orderId,
      this._estimate,
      this._paymentId,
      this._payment,
      invoicePdfId
    ));
  }

  private recreate(step: StepGuard,
    phone: Phone,
    customer: Customer,
    estimationId: string,
    customerId: string,
    orderId: string,
    estimate: Estimate,
    paymentId: string,
    payment: Payment,
    invoicePdfId: string
  ): OrderState {
    return new OrderState(
      null,
      step,
      phone,
      customer,
      estimationId,
      customerId,
      orderId,
      estimate,
      paymentId,
      payment,
      invoicePdfId
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/state/order/order-state.ts